import React, { useState, useEffect } from 'react';
import { taxAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';
import { US_STATES } from '../../utils/constants';

const EMPTY = { state_code: '', state_name: '', tax_name: 'Sales Tax', tax_rate: '', shipping_fee: '50', is_active: 1 };

const AdminTaxRates = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bulkFee, setBulkFee] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  const [bulkTaxRate, setBulkTaxRate] = useState('');
  const [bulkTaxSaving, setBulkTaxSaving] = useState(false);
  const [bulkTaxMsg, setBulkTaxMsg] = useState('');

  const load = () => {
    setLoading(true);
    taxAPI.getAll().then(r => setRates(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({ state_code: r.state_code, state_name: r.state_name, tax_name: r.tax_name, tax_rate: r.tax_rate, shipping_fee: r.shipping_fee ?? '50', is_active: r.is_active });
    setError('');
  };

  const cancelEdit = () => { setEditId(null); setForm(EMPTY); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.state_code || !form.state_name || form.tax_rate === '') {
      setError('State code, state name and tax rate are required'); return;
    }
    setSaving(true); setError('');
    try {
      const payload = { ...form, tax_rate: parseFloat(form.tax_rate), shipping_fee: parseFloat(form.shipping_fee || 50) };
      if (editId) {
        await taxAPI.update(editId, payload);
      } else {
        await taxAPI.create(payload);
      }
      cancelEdit();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete tax rate for ${name}?`)) return;
    await taxAPI.remove(id);
    load();
  };

  const toggleActive = async (r) => {
    await taxAPI.update(r.id, { is_active: r.is_active === 1 ? 0 : 1 });
    load();
  };

  const handleBulkShipping = async (e) => {
    e.preventDefault();
    if (bulkFee === '' || isNaN(parseFloat(bulkFee))) { setBulkMsg('Enter a valid amount'); return; }
    if (!window.confirm(`Set shipping fee to $${parseFloat(bulkFee).toFixed(2)} for ALL states?`)) return;
    setBulkSaving(true); setBulkMsg('');
    try {
      const res = await taxAPI.bulkUpdateShipping(parseFloat(bulkFee));
      setBulkMsg(`✓ Updated ${res.data.updated} states`);
      setBulkFee('');
      load();
    } catch (err) {
      console.error(err);
      setBulkMsg(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkTax = async (e) => {
    e.preventDefault();
    if (bulkTaxRate === '' || isNaN(parseFloat(bulkTaxRate))) { setBulkTaxMsg('Enter a valid rate'); return; }
    if (!window.confirm(`Set tax rate to ${parseFloat(bulkTaxRate).toFixed(2)}% for ALL states?`)) return;
    setBulkTaxSaving(true); setBulkTaxMsg('');
    try {
      const res = await taxAPI.bulkUpdateTax(parseFloat(bulkTaxRate));
      setBulkTaxMsg(`✓ Updated ${res.data.updated} states`);
      setBulkTaxRate('');
      load();
    } catch (err) {
      console.error(err);
      setBulkTaxMsg(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setBulkTaxSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>🌍 Tax Rates by Country</h1>
        <p>Set the applicable tax rate for each country your store ships to</p>
      </div>

      {/* Form */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{editId ? 'Edit Tax Rate' : '+ Add Country Tax Rate'}</h2>
        {error && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="grid-4" style={{ gap: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">State *</label>
              <select
                className="form-input"
                value={form.state_code}
                onChange={e => {
                  const stateCode = e.target.value;
                  const stateName = US_STATES.find(s => s.code === stateCode)?.name || '';
                  setForm(prev => ({ ...prev, state_code: stateCode, state_name: stateName }));
                }}
                disabled={!!editId}
              >
                <option value="">-- Select State --</option>
                {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tax Label</label>
              <input className="form-input" value={form.tax_name} onChange={e => set('tax_name', e.target.value)} placeholder="GST / VAT / Sales Tax" />
            </div>
            <div className="form-group">
              <label className="form-label">Tax Rate (%) *</label>
              <input className="form-input" type="number" min="0" max="100" step="0.01" value={form.tax_rate} onChange={e => set('tax_rate', e.target.value)} placeholder="10.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Shipping Fee ($)</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.shipping_fee} onChange={e => set('shipping_fee', e.target.value)} placeholder="50.00" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : (editId ? 'Update' : 'Add Rate')}</button>
            {editId && <button className="btn btn-outline" type="button" onClick={cancelEdit}>Cancel</button>}
          </div>
        </form>
      </div>

      {/* Bulk Updates */}
      <div className="grid-2" style={{ gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Bulk Shipping Update */}
        <div className="card" style={{ background: '#f0fdf4', border: '1px solid #86efac', margin: 0 }}>
          <h2 style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '1rem' }}>🚚 Apply Shipping Fee to All States</h2>
          <p style={{ fontSize: '0.83rem', color: '#374151', marginBottom: '0.75rem' }}>
            Set a single shipping fee that applies to every state at once. Individual states can still be overridden by editing them separately.
          </p>
          <form onSubmit={handleBulkShipping} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>$</span>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 50.00"
                value={bulkFee}
                onChange={e => { setBulkFee(e.target.value); setBulkMsg(''); }}
                style={{ width: 130 }}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={bulkSaving}>
              {bulkSaving ? 'Updating…' : 'Apply to All'}
            </button>
            {bulkMsg && (
              <span style={{ fontSize: '0.83rem', color: bulkMsg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{bulkMsg}</span>
            )}
          </form>
        </div>

        {/* Bulk Tax Update */}
        <div className="card" style={{ background: '#eff6ff', border: '1px solid #93c5fd', margin: 0 }}>
          <h2 style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '1rem' }}>🏛️ Apply Tax Rate to All States</h2>
          <p style={{ fontSize: '0.83rem', color: '#374151', marginBottom: '0.75rem' }}>
            Set a single tax percentage that applies to every state at once. Individual states can still be overridden.
          </p>
          <form onSubmit={handleBulkTax} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 10.00"
                value={bulkTaxRate}
                onChange={e => { setBulkTaxRate(e.target.value); setBulkTaxMsg(''); }}
                style={{ width: 130 }}
              />
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>%</span>
            </div>
            <button className="btn btn-primary" type="submit" disabled={bulkTaxSaving}>
              {bulkTaxSaving ? 'Updating…' : 'Apply to All'}
            </button>
            {bulkTaxMsg && (
              <span style={{ fontSize: '0.83rem', color: bulkTaxMsg.startsWith('✓') ? '#2563eb' : '#dc2626', fontWeight: 600 }}>{bulkTaxMsg}</span>
            )}
          </form>
        </div>
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>State</th>
                  <th>Country</th>
                  <th>Tax Label</th>
                  <th>Tax Rate (%)</th>
                  <th>Shipping Fee</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem' }}>No tax rates configured</td></tr>
                )}
                {rates.map(r => (
                  <tr key={r.id}>
                    <td><span style={{ fontFamily: 'monospace', fontWeight: 700, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{r.state_code}</span></td>
                    <td>{r.state_name}</td>
                    <td>{r.tax_name}</td>
                    <td><strong>{parseFloat(r.tax_rate).toFixed(2)}%</strong></td>
                    <td><strong>${parseFloat(r.shipping_fee ?? 50).toFixed(2)}</strong></td>
                    <td>
                      <button
                        className={`badge ${r.is_active === 1 ? 'badge-success' : 'badge-danger'}`}
                        style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}
                        onClick={() => toggleActive(r)}
                        title="Click to toggle"
                      >
                        {r.is_active === 1 ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-sm btn-outline" onClick={() => startEdit(r)}>Edit</button>
                        <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#dc2626' }} onClick={() => handleDelete(r.id, r.state_name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTaxRates;
